import { useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { normalizeTags } from "@/tags"
import {
  transport,
  useTransportCollectionQuery,
  type TransportCollectionData,
  type TransportRequestError,
} from "@/transport"
import type { UseQueryResult } from "@tanstack/react-query"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  mapTransportMetricsToRecordData,
  METRICS_LIST_TRANSPORT_REQUEST,
  type ListMetricsTransportRequest,
  type TransportMetricResource,
} from "../utils/transportMetricData"

type MetricsData = Record<string, MetricRecordData & { id: string }>

type MetricsTransportData = TransportCollectionData<
  "listMetrics",
  TransportMetricResource
>

type MetricsListResult<T> = Omit<
  UseQueryResult<MetricsTransportData, TransportRequestError>,
  "data" | "isLoading"
> & {
  data: T | undefined
  isLoading: boolean
}

interface UseMetricsListRecordDataOptions<T> {
  enabled?: boolean
  metricNames?: string[]
  select?: (data: MetricsData) => T
  tagFilters?: string[]
}

interface TransportLoadingStateInput {
  data: unknown
  enabled: boolean
  error: unknown
  isLoading: boolean
}

function isMetricTransportQueryLoading({
  data,
  enabled,
  error,
  isLoading,
}: TransportLoadingStateInput): boolean {
  return isLoading || (enabled && data == null && error == null)
}

export function useMetricsListRecordDataTransport<T = MetricsData>(
  options?: UseMetricsListRecordDataOptions<T>,
): MetricsListResult<T> {
  const { enabled, metricNames, select, tagFilters } = options ?? {}
  const queryEnabled = enabled ?? true
  const normalizedTagFilters = useMemo(
    () => normalizeTags(tagFilters ?? []),
    [tagFilters],
  )
  const request = useMemo<ListMetricsTransportRequest>(() => {
    if (!metricNames?.length && normalizedTagFilters.length === 0) {
      return METRICS_LIST_TRANSPORT_REQUEST
    }

    return {
      ...METRICS_LIST_TRANSPORT_REQUEST,
      metric_names: metricNames?.length ? metricNames : undefined,
      tag_filters:
        normalizedTagFilters.length > 0
          ? (normalizedTagFilters as ListMetricsTransportRequest["tag_filters"])
          : undefined,
    }
  }, [metricNames, normalizedTagFilters])
  const query = useTransportCollectionQuery<
    "listMetrics",
    TransportMetricResource
  >({
    operation: transport.collections.listMetrics,
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
    },
  })
  const metricRecords = useMemo(
    () =>
      query.data
        ? mapTransportMetricsToRecordData(query.data.items)
        : undefined,
    [query.data],
  )
  const selectedData = useMemo<T | undefined>(
    () =>
      metricRecords
        ? select
          ? select(metricRecords)
          : (metricRecords as T)
        : undefined,
    [metricRecords, select],
  )

  return {
    ...query,
    data: selectedData,
    isLoading: isMetricTransportQueryLoading({
      data: query.data,
      enabled: queryEnabled,
      error: query.error,
      isLoading: query.isLoading,
    }),
  }
}
